<template>
  <div class="container-fluid">

    <!-- ── Filtros ── -->
    <div class="row mt-3">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0">
              <vue-feather type="filter" class="me-1"></vue-feather> Filtros
            </h6>
          </div>
          <div class="card-body">
            <div class="row g-3 align-items-end">

              <!-- Programa -->
              <div class="col-md-4">
                <label class="form-label fw-semibold">Programa</label>
                <div v-if="loadingProgramas" class="text-muted small py-1">Cargando…</div>
                <div v-else-if="errorProgramas" class="text-danger small">{{ errorProgramas }}</div>
                <select v-else v-model="programaSeleccionado" class="form-select" @change="onProgramaChange">
                  <option :value="null" disabled>— Elige un programa —</option>
                  <option v-for="p in programas" :key="p.id" :value="p">{{ p.nombre }}</option>
                </select>
              </div>

              <!-- Métricas (multi-select) -->
              <div class="col-md-6">
                <label class="form-label fw-semibold">
                  Métricas
                  <span class="text-muted fw-normal small">(opcional — vacío muestra todas)</span>
                </label>
                <div v-if="loadingMetricas" class="text-muted small py-1">Cargando métricas…</div>
                <div v-else-if="!programaSeleccionado" class="text-muted small py-1">Primero selecciona un programa.</div>
                <div v-else class="d-flex flex-wrap gap-2 mt-1">
                  <div
                    v-for="m in metricas"
                    :key="m.id"
                    class="badge-check"
                    :class="{ selected: metricasSeleccionadas.includes(m.id) }"
                    @click="toggleMetrica(m.id)"
                  >
                    {{ m.nombre_metrica }}
                  </div>
                </div>
              </div>

              <!-- Botón -->
              <div class="col-md-2 d-flex align-items-end">
                <button
                  class="btn btn-primary w-100"
                  :disabled="!programaSeleccionado || loadingResultados"
                  @click="cargarResultados"
                >
                  <vue-feather type="search" size="14" class="me-1"></vue-feather>
                  <span v-if="loadingResultados">
                    <span class="spinner-border spinner-border-sm me-1"></span>
                  </span>
                  <span v-else>Ver resultados</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Sin resultados ── -->
    <div v-if="!cargado && !loadingResultados" class="text-center py-5 text-muted">
      <vue-feather type="bar-chart" size="48" class="mb-2 opacity-50"></vue-feather>
      <p>Selecciona un programa y presiona <strong>Ver resultados</strong>.</p>
    </div>

    <!-- ── Error global ── -->
    <div v-if="errorResultados" class="row mt-3">
      <div class="col-12">
        <div class="alert alert-danger">{{ errorResultados }}</div>
      </div>
    </div>

    <!-- ── Sin datos ── -->
    <div v-if="cargado && resultadosPorMetrica.length === 0 && !loadingResultados" class="text-center py-5 text-muted">
      <p>No se encontraron registros históricos para los filtros seleccionados.</p>
    </div>

    <!-- ── Fila por métrica ── -->
    <div
      v-for="rm in resultadosPorMetrica"
      :key="rm.metrica.id"
      class="row mt-4"
    >
      <!-- Encabezado de métrica -->
      <div class="col-12 mb-2">
        <h6 class="fw-bold" style="color:#182e50;">
          <vue-feather type="bar-chart-2" size="16" class="me-1"></vue-feather>
          {{ rm.metrica.nombre_metrica }}
          <span class="badge bg-secondary ms-2 fw-normal">{{ rm.metrica.unidad_medida }}</span>
          <span class="badge bg-light text-dark ms-1 fw-normal border">{{ rm.metrica.periodicidad }}</span>
        </h6>
      </div>

      <!-- Tabla -->
      <div class="col-md-5">
        <div class="card h-100">
          <div class="card-header py-2">
            <small class="fw-semibold text-muted"><vue-feather type="list" size="13" class="me-1"></vue-feather> Historial</small>
          </div>
          <div class="card-body p-0" style="max-height:220px; overflow-y:auto;">
            <div v-if="rm.error" class="text-center py-4 text-danger small">
              Error al cargar el historial de esta métrica.
            </div>
            <div v-else-if="rm.historial.length === 0" class="text-center py-4 text-muted small">
              Sin registros históricos.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Corte</th>
                    <th class="text-end">Programado</th>
                    <th class="text-end">Real</th>
                    <th class="text-end">Presupuesto</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="h in rm.historial" :key="h.id">
                    <td class="text-nowrap">{{ h.fecha_corte }}</td>
                    <td class="text-end">{{ h.valor_programado }}</td>
                    <td class="text-end">
                      <span
                        :class="h.valor_real >= h.valor_programado ? 'text-success fw-semibold' : 'text-danger fw-semibold'"
                      >{{ h.valor_real }}</span>
                    </td>
                    <td class="text-end text-muted">{{ h.presupuesto_ejercido ?? '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Gráfica -->
      <div class="col-md-7">
        <div class="card h-100">
          <div class="card-header py-2">
            <small class="fw-semibold text-muted"><vue-feather type="trending-up" size="13" class="me-1"></vue-feather> Tendencia</small>
          </div>
          <div class="card-body" style="padding:0.75rem;">
            <div style="position:relative; height:220px;">
              <Line
                :id="`chart-metrica-${rm.metrica.id}`"
                :key="`chart-${rm.metrica.id}`"
                :data="rm.chartData"
                :options="chartOptions"
              />
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import api from "@/axios";
import authService from "@/services/authService";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export default {
  name: "EvaluacionesResultados",
  components: { Line },

  data() {
    return {
      // Filtros
      programas: [],
      loadingProgramas: false,
      errorProgramas: null,
      programaSeleccionado: null,

      metricas: [],
      loadingMetricas: false,
      metricasSeleccionadas: [], // array of ids

      // Resultados
      resultadosPorMetrica: [],
      loadingResultados: false,
      errorResultados: null,
      cargado: false,

      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
          tooltip: { mode: "index", intersect: false },
        },
        scales: {
          x: { ticks: { maxRotation: 45, minRotation: 30 } },
          y: { beginAtZero: true },
        },
        interaction: { mode: "nearest", axis: "x", intersect: false },
      },
    };
  },

  created() {
    this.fetchProgramas();
  },

  methods: {
    async fetchProgramas() {
      this.loadingProgramas = true;
      this.errorProgramas = null;
      try {
        const ud = authService.getUserData();
        const userId = ud?.id ?? ud?.pk;
        const { data } = await api.get(`usuarios/${userId}/programas/`);
        this.programas = Array.isArray(data) ? data : (data.programas ?? data.results ?? []);
      } catch (err) {
        this.errorProgramas = err.response?.data?.detail ?? "Error al cargar los programas.";
      } finally {
        this.loadingProgramas = false;
      }
    },

    async onProgramaChange() {
      this.metricasSeleccionadas = [];
      this.metricas = [];
      this.resultadosPorMetrica = [];
      this.cargado = false;
      if (!this.programaSeleccionado) return;
      this.loadingMetricas = true;
      try {
        const { data } = await api.get(`metricas-config/${this.programaSeleccionado.id}`);
        this.metricas = Array.isArray(data) ? data : (data.results ?? []);
      } finally {
        this.loadingMetricas = false;
      }
    },

    toggleMetrica(id) {
      const idx = this.metricasSeleccionadas.indexOf(id);
      if (idx === -1) this.metricasSeleccionadas.push(id);
      else this.metricasSeleccionadas.splice(idx, 1);
    },

    async cargarResultados() {
      this.loadingResultados = true;
      this.errorResultados = null;
      this.resultadosPorMetrica = [];
      this.cargado = false;

      // Determinar qué métricas mostrar
      const metricasFiltro = this.metricasSeleccionadas.length > 0
        ? this.metricas.filter(m => this.metricasSeleccionadas.includes(m.id))
        : this.metricas;

      try {
        const settled = await Promise.allSettled(
          metricasFiltro.map(async (metrica) => {
            const { data } = await api.get(`metricas-historial/${metrica.id}`);

            // Normalizar respuesta: lista, paginada u objeto único
            let raw;
            if (Array.isArray(data)) {
              raw = data;
            } else if (data && Array.isArray(data.results)) {
              raw = data.results;
            } else if (data && typeof data === "object" && data.id !== undefined) {
              raw = [data]; // objeto único envuelto en array
            } else {
              raw = [];
            }

            const historial = raw.sort(
              (a, b) => new Date(a.fecha_corte) - new Date(b.fecha_corte)
            );

            const labels = historial.map(h => h.fecha_corte);
            const chartData = {
              labels,
              datasets: [
                {
                  label: "Programado",
                  data: historial.map(h => h.valor_programado),
                  borderColor: "#6c8ebf",
                  backgroundColor: "rgba(108,142,191,0.1)",
                  borderDash: [5, 5],
                  fill: false,
                  tension: 0.3,
                  pointRadius: 4,
                },
                {
                  label: "Real",
                  data: historial.map(h => h.valor_real),
                  borderColor: "#182e50",
                  backgroundColor: "rgba(24,46,80,0.12)",
                  fill: true,
                  tension: 0.3,
                  pointRadius: 4,
                },
              ],
            };

            return { metrica, historial, chartData };
          })
        );

        // Incluir TODAS las métricas; las fallidas muestran error inline
        this.resultadosPorMetrica = settled.map((s, i) => {
          if (s.status === "fulfilled") return s.value;
          return { metrica: metricasFiltro[i], historial: [], chartData: { labels: [], datasets: [] }, error: true };
        });

        this.cargado = true;
      } catch (err) {
        this.errorResultados = err.response?.data?.detail ?? "Error al cargar el historial.";
      } finally {
        this.loadingResultados = false;
      }
    },
  },
};
</script>

<style scoped>
.badge-check {
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #ced4da;
  background: #f8f9fa;
  font-size: 0.8rem;
  user-select: none;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.badge-check.selected {
  background: #182e50;
  color: #fff;
  border-color: #182e50;
}
.badge-check:hover:not(.selected) {
  background: #e9ecef;
}
</style>
